import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import type { ComputedRef, Ref, ShallowRef } from 'vue';
import SectorsService from '@/api/services/sectors.service';
import TablesService from '@/api/services/tables.service';
import {
  groupAreas,
  groupTableOptions,
  unplacedGroupTables,
} from '@/domain/reservations/reservation-group-tables';
import type {
  IReservationGroupArea,
  IReservationGroupTableOption,
} from '@/domain/reservations/reservation-group-tables';
import type { IReservationTableRef } from '@/interfaces/IReservation';
import type { ISector } from '@/interfaces/ISector';
import type { ITable } from '@/interfaces/ITable';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { IReservationGuideScope } from './reservation-guide-context';

export interface IReservationFloorPlan {
  status: Ref<'idle' | 'loading' | 'ready' | 'error'>;
  sectors: ShallowRef<ISector[]>;
  layout: ShallowRef<ITable[]>;
  /** The given tables with their floor-plan placement. */
  options: ComputedRef<IReservationGroupTableOption[]>;
  areas: ComputedRef<IReservationGroupArea[]>;
  unplaced: ComputedRef<IReservationGroupTableOption[]>;
  /** Fetches sectors and placement; the caller decides when (first show, mount, retry). */
  load(): Promise<void>;
}

/**
 * Sectors and table placement of the scope's branch, merged with the tables
 * the caller shows (draft rows in the guide and the card). Extracted from the
 * group modal's loader, which keeps its own copy on purpose.
 */
export function useReservationFloorPlan(
  scope: () => IReservationGuideScope | null,
  tables: () => readonly IReservationTableRef[]
): IReservationFloorPlan {
  const store = useRestaurantStore();
  const status = ref<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const sectors = shallowRef<ISector[]>([]);
  const layout = shallowRef<ITable[]>([]);
  let sequence = 0;
  let alive = true;
  const key = () => {
    const current = scope();
    return current ? `${current.restaurantId}:${current.branchId}` : '';
  };
  const options = computed(() => groupTableOptions(tables(), layout.value));
  const areas = computed(() => {
    const current = scope();
    return current
      ? groupAreas(sectors.value, current.branchId, options.value)
      : [];
  });
  const unplaced = computed(() =>
    unplacedGroupTables(areas.value, options.value)
  );

  async function load(): Promise<void> {
    const current = scope();
    const request = ++sequence;
    if (!current) {
      status.value = 'idle';
      return;
    }
    const requestKey = key();
    const stale = () => !alive || request !== sequence || key() !== requestKey;
    status.value = 'loading';
    try {
      const [sectorList, tableList] = await Promise.all([
        SectorsService.getAll(current.restaurantId, current.branchId),
        TablesService.getAll(current.restaurantId, current.branchId),
      ]);
      if (stale()) return;
      sectors.value = sectorList;
      layout.value = tableList;
      // Other views name sectors from the store; the layout stays local.
      store.setSectors(sectorList);
      status.value = 'ready';
    } catch {
      if (stale()) return;
      status.value = 'error';
    }
  }

  watch(key, () => {
    sequence++;
    sectors.value = [];
    layout.value = [];
    status.value = 'idle';
  });
  onScopeDispose(() => {
    alive = false;
    sequence++;
  });

  return { status, sectors, layout, options, areas, unplaced, load };
}
